import { memo, useCallback, useRef } from 'react';

import { SidebarResizer } from '@air/component-sidebar';

import { WORKSPACE_AGENT_PANEL_WIDTH_CSS_VARIABLE } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import {
  WORKSPACE_AGENT_PANEL_DEFAULT_WIDTH,
  WORKSPACE_AGENT_PANEL_MAX_WIDTH,
  WORKSPACE_AGENT_PANEL_MIN_WIDTH,
} from '~/constants/panelWidths';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

const readWorkspaceAgentPanelWidthFromCss = (): number | null => {
  const raw = document.documentElement.style.getPropertyValue(WORKSPACE_AGENT_PANEL_WIDTH_CSS_VARIABLE);
  const parsed = Number.parseFloat(raw);

  return Number.isFinite(parsed) ? parsed : null;
};

export const WorkspaceAgentPanelDockedResizer = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const width = useWorkspaceAgentPanelStore((state) => state.width);
  const setWidth = useWorkspaceAgentPanelStore((state) => state.setWidth);
  const setIsResizing = useWorkspaceAgentPanelStore((state) => state.setIsResizing);
  const isDraggingRef = useRef<boolean>();

  const commitWidthFromCss = useCallback(() => {
    const parsed = readWorkspaceAgentPanelWidthFromCss();

    if (parsed !== null) {
      setWidth(parsed);
    }
  }, [setWidth]);

  const handleResizeStart = useCallback(() => {
    isDraggingRef.current = true;
    setIsResizing(true);
  }, [setIsResizing]);

  const handleResizeEnd = useCallback(() => {
    isDraggingRef.current = false;
    setIsResizing(false);
    commitWidthFromCss();
  }, [commitWidthFromCss, setIsResizing]);

  const handleResize = useCallback(
    (nextWidth: number) => {
      if (!isDraggingRef.current) {
        setWidth(nextWidth);
      }
    },
    [setWidth],
  );

  const handleResetToDefaultWidth = useCallback(() => {
    setIsResizing(false);
    setWidth(WORKSPACE_AGENT_PANEL_DEFAULT_WIDTH);
  }, [setIsResizing, setWidth]);

  if (!ui2026 || dock !== 'right') {
    return null;
  }

  return (
    <SidebarResizer
      className="w-2.5 items-start"
      cssVariable={WORKSPACE_AGENT_PANEL_WIDTH_CSS_VARIABLE}
      externalValue={width}
      localStorageKey=""
      maxSize={WORKSPACE_AGENT_PANEL_MAX_WIDTH}
      minSize={WORKSPACE_AGENT_PANEL_MIN_WIDTH}
      onReset={handleResetToDefaultWidth}
      onResize={handleResize}
      onResizeEnd={handleResizeEnd}
      onResizeStart={handleResizeStart}
      showIndicator={false}
      side="right"
      size={WORKSPACE_AGENT_PANEL_DEFAULT_WIDTH}
    />
  );
});

WorkspaceAgentPanelDockedResizer.displayName = 'WorkspaceAgentPanelDockedResizer';
